'use client'

import { useState } from 'react'
import { cn } from '../../utils/cn'
import type { ChecklistProps, ChecklistStep } from './types'

function StatusIcon({ status }: { status: ChecklistStep['status'] }) {
  if (status === 'completed') {
    return (
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 border border-primary/40">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-primary">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    )
  }
  if (status === 'in_progress') {
    return (
      <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-primary animate-pulse">
        <div className="w-2 h-2 rounded-full bg-primary" />
      </div>
    )
  }
  if (status === 'skipped') {
    return <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-border border-dashed opacity-40" />
  }
  return <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-border" />
}

export function Checklist({ title, steps, onStepComplete, onAllComplete, dismissable, onDismiss, className }: ChecklistProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const completed = steps.filter(s => s.status === 'completed').length

  return (
    <div className={cn('rounded-xl border border-border bg-card', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{completed} / {steps.length} completed</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress ring */}
          <svg width="32" height="32" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15" fill="none" stroke="hsl(var(--border))" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15" fill="none" stroke="hsl(var(--primary))" strokeWidth="3"
              strokeDasharray={`${(completed / steps.length) * 94.25} 94.25`}
              strokeLinecap="round" transform="rotate(-90 18 18)"
              style={{ transition: 'stroke-dasharray 0.5s ease' }}
            />
          </svg>
          {dismissable && onDismiss && (
            <button onClick={onDismiss} className="text-muted-foreground hover:text-foreground transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* Steps */}
      <div className="divide-y divide-border">
        {steps.map(step => {
          const isExpanded = expandedId === step.id
          return (
            <div key={step.id}>
              <button
                onClick={() => setExpandedId(isExpanded ? null : step.id)}
                className="flex items-center gap-3 w-full px-5 py-3 text-left hover:bg-muted/30 transition-colors"
              >
                <StatusIcon status={step.status} />
                <div className="flex-1 min-w-0">
                  <span className={cn('text-sm font-medium', step.status === 'completed' ? 'text-muted-foreground line-through' : 'text-foreground')}>
                    {step.label}
                  </span>
                  {step.description && !isExpanded && (
                    <p className="text-xs text-muted-foreground truncate">{step.description}</p>
                  )}
                </div>
                {(step.children || step.description) && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    className={cn('text-muted-foreground transition-transform', isExpanded && 'rotate-180')}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </button>
              {isExpanded && (
                <div className="px-5 pb-4 pl-14">
                  {step.description && <p className="text-sm text-muted-foreground mb-3">{step.description}</p>}
                  {step.children}
                  {step.status !== 'completed' && onStepComplete && (
                    <button
                      onClick={() => {
                        onStepComplete(step.id)
                        if (completed + 1 === steps.length && onAllComplete) onAllComplete()
                      }}
                      className="mt-3 px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      Mark Complete
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
